import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { Button } from "./ui/button";
import { Switch } from "./ui/switch";
import { Cookie, X } from "@phosphor-icons/react";

const CONSENT_KEY = "nc_cookie_consent";
const CONSENT_VERSION = "1.0";

/**
 * DSGVO-konformer Cookie-Banner.
 * - Erscheint, wenn noch keine Zustimmung gespeichert wurde
 * - Drei Optionen: „Alle akzeptieren", „Nur Notwendige", „Auswahl anpassen"
 * - Speichert die Auswahl in localStorage (Opt-in für alle Nicht-Essential-Cookies)
 * - Lässt sich nachträglich über Footer-Link „Cookie-Einstellungen" wieder öffnen
 */
export default function CookieConsent() {
  const [visible, setVisible] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [prefs, setPrefs] = useState({ essential: true, functional: false, analytics: false });

  const loadConsent = useCallback(() => {
    try {
      const raw = localStorage.getItem(CONSENT_KEY);
      if (!raw) return null;
      const parsed = JSON.parse(raw);
      if (parsed.version !== CONSENT_VERSION) return null;
      return parsed;
    } catch {
      return null;
    }
  }, []);

  useEffect(() => {
    const consent = loadConsent();
    if (!consent) {
      setVisible(true);
    } else if (consent.prefs) {
      setPrefs(consent.prefs);
    }
  }, [loadConsent]);

  useEffect(() => {
    const handler = () => { setShowSettings(true); setVisible(true); };
    window.addEventListener("nc:open-cookie-settings", handler);
    return () => window.removeEventListener("nc:open-cookie-settings", handler);
  }, []);

  const save = (newPrefs) => {
    try {
      localStorage.setItem(CONSENT_KEY, JSON.stringify({
        version: CONSENT_VERSION,
        prefs: newPrefs,
        timestamp: new Date().toISOString(),
      }));
    } catch {
      // ignore quota errors
    }
    setPrefs(newPrefs);
    setVisible(false);
    setShowSettings(false);
  };

  const acceptAll  = () => save({ essential: true, functional: true, analytics: true });
  const rejectAll  = () => save({ essential: true, functional: false, analytics: false });
  const saveChoice = () => save({ ...prefs, essential: true });

  if (!visible) return null;

  return (
    <div className="fixed inset-0 z-[100] flex items-end sm:items-center justify-center bg-zinc-900/50 backdrop-blur-sm p-3 sm:p-6"
      data-testid="cookie-consent-banner">
      <div className="w-full max-w-2xl bg-white border border-zinc-200 rounded-sm shadow-2xl overflow-hidden">
        {/* Header */}
        <div className="px-5 sm:px-7 pt-5 pb-3 flex items-start gap-3">
          <div className="w-9 h-9 rounded-sm bg-[#002FA7]/10 flex items-center justify-center flex-shrink-0">
            <Cookie size={20} weight="duotone" className="text-[#002FA7]" />
          </div>
          <div className="flex-1">
            <h2 className="font-heading text-lg sm:text-xl font-bold tracking-tight">
              Wir respektieren deine Privatsphäre
            </h2>
            <p className="text-sm text-zinc-600 mt-1 leading-relaxed">
              Diese Seite verwendet Cookies, um die App bereitzustellen, dich angemeldet zu halten
              und — wenn du zustimmst — anonyme Nutzungsstatistiken zu erfassen.
              Du kannst deine Auswahl jederzeit über den Link „Cookie-Einstellungen" im Footer ändern.
            </p>
          </div>
        </div>

        {/* Settings (toggle) */}
        {showSettings && (
          <div className="px-5 sm:px-7 pb-4 space-y-3 border-t border-zinc-100 pt-4">
            {[
              {
                key: "essential",
                title: "Notwendig (erforderlich)",
                desc: "Login-Cookies, Sitzungsdaten, Sicherheit. Ohne diese funktioniert die App nicht.",
                locked: true,
              },
              {
                key: "functional",
                title: "Funktional",
                desc: "Speichert UI-Einstellungen (z. B. Filter, Sortierung), damit du nicht alles neu anklicken musst.",
              },
              {
                key: "analytics",
                title: "Statistik",
                desc: "Anonyme Nutzungs-Statistiken, damit wir die App verbessern können. Keine Weitergabe an Dritte.",
              },
            ].map((c) => (
              <div key={c.key} className="flex items-start gap-3 py-1">
                <div className="flex-1">
                  <div className="font-medium text-sm">{c.title}</div>
                  <div className="text-xs text-zinc-500 leading-relaxed">{c.desc}</div>
                </div>
                <Switch
                  checked={c.locked ? true : !!prefs[c.key]}
                  disabled={c.locked}
                  onCheckedChange={(v) => setPrefs((p) => ({ ...p, [c.key]: !!v }))}
                  data-testid={`cookie-toggle-${c.key}`}
                />
              </div>
            ))}
          </div>
        )}

        {/* Buttons */}
        <div className="px-5 sm:px-7 py-4 bg-zinc-50 border-t border-zinc-200 flex flex-col-reverse sm:flex-row gap-2 sm:gap-3 sm:justify-end">
          {!showSettings ? (
            <>
              <Button variant="ghost" onClick={() => setShowSettings(true)} className="rounded-sm text-sm"
                data-testid="cookie-customize">
                Auswahl anpassen
              </Button>
              <Button variant="outline" onClick={rejectAll} className="rounded-sm text-sm"
                data-testid="cookie-reject">
                Nur Notwendige
              </Button>
              <Button onClick={acceptAll}
                className="bg-[#002FA7] hover:bg-[#00248a] rounded-sm text-sm font-medium"
                data-testid="cookie-accept-all">
                Alle akzeptieren
              </Button>
            </>
          ) : (
            <>
              <Button variant="outline" onClick={rejectAll} className="rounded-sm text-sm"
                data-testid="cookie-reject">
                Nur Notwendige
              </Button>
              <Button onClick={saveChoice}
                className="bg-[#002FA7] hover:bg-[#00248a] rounded-sm text-sm font-medium"
                data-testid="cookie-save">
                Auswahl speichern
              </Button>
            </>
          )}
        </div>

        {/* Legal links */}
        <div className="px-5 sm:px-7 py-2.5 bg-zinc-50 border-t border-zinc-200 text-[11px] text-zinc-500 flex flex-wrap gap-x-4 gap-y-1 justify-end">
          <Link to="/datenschutz" className="hover:text-zinc-900" data-testid="cookie-legal-privacy">Datenschutz</Link>
          <Link to="/impressum" className="hover:text-zinc-900">Impressum</Link>
          <Link to="/agb" className="hover:text-zinc-900">AGB</Link>
        </div>
      </div>
    </div>
  );
}

// Helper: kann von anderen Komponenten genutzt werden, um zu prüfen, ob ein Cookie-Typ erlaubt ist
export function hasConsent(type) {
  try {
    const raw = localStorage.getItem(CONSENT_KEY);
    if (!raw) return type === "essential";
    const parsed = JSON.parse(raw);
    if (parsed.version !== CONSENT_VERSION) return type === "essential";
    return !!parsed.prefs?.[type];
  } catch {
    return type === "essential";
  }
}
